import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import { push } from 'react-router-redux';
import { call, put, select } from 'redux-saga/effects';

import * as Utils from '../../utils';

import * as D from './deps';
import * as CatalogServices from './services';
import * as CatalogUtils from './utils';

const log = D.Logger.child({ component: 'src/domains/catalog/sagas/' }, true);

function* getArtistDetails(pandoraId) {
  const artistDetails = yield select(CatalogUtils.getCatalogDetailsFromState, pandoraId);
  if (artistDetails.get('topTracks')) {
    return artistDetails;
  }
  yield call(CatalogServices.getDetails, { pandoraId, withCollaborations: true });
  return yield select(CatalogUtils.getCatalogDetailsFromState, pandoraId);
}

// workers
export function* onGetTrack({ payload: { pandoraId } }) {
  const track = yield select(CatalogUtils.getAnnotations, pandoraId);

  if (!track.isEmpty()) {
    const trackDetails = yield select(CatalogUtils.getCatalogDetailsFromState, pandoraId);
    if (!trackDetails || trackDetails.isEmpty()) {
      yield call(CatalogServices.getDetails, { pandoraId });
    }
  }
}

export function* onGetAlbum({ payload: { pandoraId } }) {
  const album = yield select(CatalogUtils.getAnnotations, pandoraId);

  if (!album.isEmpty()) {
    const missingIds = yield select(CatalogUtils.getIdsOfMissingAnnotations, album.get('tracks'));

    if (!missingIds.isEmpty()) {
      yield call(CatalogServices.getDetails, { pandoraId });
    }
  }

  if (album.isEmpty()) {
    yield call(CatalogServices.getDetails, { pandoraId });
  }
}

export function* onGetArtistTopSongs({ payload: { pandoraId } }) {
  const artistDetails = yield call(getArtistDetails, pandoraId);
  const missingIds = yield select(
    CatalogUtils.getIdsOfMissingAnnotations,
    artistDetails.get('topTracks'),
  );
  if (!missingIds.isEmpty()) {
    yield call(CatalogServices.annotateObjectsSimple, {
      pandoraIds: missingIds.toArray(),
    });
  }
}

/**
 * Hydrates the store with annotated track info for all of an artist's tracks
 *
 * @param {object} action
 * @param action.payload
 * @param action.payload.pandoraId
 */
export function* onGetArtistAllSongs({ payload: { pandoraId } }) {
  let artistTrackIds = yield select(CatalogUtils.getAllArtistTracksFromState, pandoraId);
  if (artistTrackIds.isEmpty()) {
    yield call(getAllArtistTracks, { payload: { artistPandoraId: pandoraId } });
    artistTrackIds = yield select(CatalogUtils.getAllArtistTracksFromState, pandoraId);
  }

  const missingTrackIds = yield select(CatalogUtils.getIdsOfMissingAnnotations, artistTrackIds);
  if (!missingTrackIds.isEmpty()) {
    yield call(CatalogServices.annotateObjectsSimple, {
      pandoraIds: missingTrackIds.toArray(),
    });
  }
}

export function* annotateWorker({ payload: { ids, annotateAlbumTracks } }) {
  yield call(CatalogServices.annotateObjects, {
    pandoraIds: ids,
    annotateAlbumTracks,
  });
}

export function* annotateObjectsSimple({ payload: { ids, annotateAlbumTracks } }) {
  yield call(CatalogServices.annotateObjectsSimple, {
    pandoraIds: ids,
    annotateAlbumTracks,
  });
}

export function* getDetailsWorker({
  payload: { pandoraId, redirectOnNotFound, withCollaborations },
}) {
  const id = isArray(pandoraId) ? pandoraId[0] : pandoraId;
  try {
    yield call(CatalogServices.getDetails, {
      pandoraId: id,
      withCollaborations,
    });
  } catch (e) {
    if (e.errorType === D.ApiErrorTypes.NOT_FOUND && redirectOnNotFound) {
      yield put(push(D.Utils.Route.getNotFoundPath()));
    } else {
      throw e;
    }
  }
}

export function* getArtistDiscography({
  payload: { artistPandoraId, annotationLimit, withCollaborations },
}) {
  yield call(CatalogServices.getArtistDiscography, {
    annotationLimit,
    artistPandoraId,
    withCollaborations,
  });
}

export function* getFullLyrics({ payload: { returnExplicit, trackPandoraId } }) {
  const lyrics = yield select(Utils.Music.getFullLyricsFromState, trackPandoraId);

  if (!lyrics) {
    yield call(CatalogServices.getFullLyrics, {
      returnExplicit,
      trackPandoraId,
    });
  }
}

export function* getArtistEvents({ payload: { artistPandoraId } }) {
  yield call(CatalogServices.getArtistEvents, {
    pandoraId: artistPandoraId,
  });
}

export function* getAllArtistTracks({
  payload: { artistPandoraId, annotationLimit, withCollaborations },
}) {
  yield call(CatalogServices.getAllArtistTracks, {
    annotationLimit,
    artistPandoraId,
    withCollaborations,
  });
}

// This function is triggered whenever an entity's rights info have expired.
// We grab the expiration timestamp of said annotation, search for all annotations
// with a plus/minus 1 second interval of the timestamp, and annotate all of them at once
export function* refreshAnnotationsWorker({ payload: { pandoraId } }) {
  const triggeredAnnotation = yield select(CatalogUtils.getAnnotations, pandoraId);
  const timestampToCheck = triggeredAnnotation.getIn(['rightsInfo', 'expirationTime']);
  const annotations = yield select(D.Utils.Entity.getAllAnnotations);
  const listOfIds = annotations
    .filter(annotation => {
      // grab ids with similar expiration times (within a plus/minus range of 1 second)
      const currTimestamp = annotation.getIn(['rightsInfo', 'expirationTime']);
      return Math.abs(currTimestamp - timestampToCheck <= 1000);
    })
    .map(item => item.get('pandoraId'))
    .toArray(); // API expects an array, not an object

  log.info(`refreshAnnotationsWorker(): annotating items ${JSON.stringify(listOfIds)}`);
  yield call(CatalogServices.annotateObjectsSimple, { pandoraIds: listOfIds });
}

export function* ensureFullDataWorker(action) {
  const { ids, idFetcher, idVerifier } = action.payload;
  const idsToGet = [];
  const state = yield select();
  ids.forEach(id => {
    if (!isNil(id) && !idVerifier(state, id)) {
      idsToGet.push(id);
    }
  });
  if (idsToGet.length) {
    yield put(idFetcher(state, idsToGet));
  }
}
